import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Mail, Bell, MessageSquare, ShieldCheck, CreditCard, Users, RefreshCw, History } from "lucide-react";

const COST_META = {
  ai_insight: { label: "AI Match Insights", icon: Sparkles, billed: "Emergent LLM key — per request", color: "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200" },
  email: { label: "Emails Sent", icon: Mail, billed: "Resend (Emergent-managed)", color: "text-sky-700 bg-sky-50 border-sky-200" },
  push: { label: "Push Notifications", icon: Bell, billed: "Free (FCM / Expo)", color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  email_pin: { label: "Login PINs (Email)", icon: MessageSquare, billed: "Resend (Emergent-managed)", color: "text-amber-700 bg-amber-50 border-amber-200" },
};

const CUR_SYMBOL = { gbp: "£", usd: "$", eur: "€", inr: "₹" };

function Stat({ label, value }) {
  return (
    <div className="text-center px-3">
      <div className="text-2xl font-semibold text-slate-900 tabular-nums" data-testid={`stat-${label}`}>{value}</div>
      <div className="text-xs uppercase tracking-wide text-slate-500 mt-1">{label}</div>
    </div>
  );
}

export default function AdminUsage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/usage")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (user?.role === "admin") load(); /* eslint-disable-next-line */ }, [user?.role]);

  if (user && user.role !== "admin") {
    return <div className="max-w-3xl mx-auto p-10"><Card className="p-8 text-center">Admin only.</Card></div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="admin-usage-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <History className="w-6 h-6 text-[#6B1D2F]" />
          <div>
            <h1 className="font-serif text-2xl text-slate-900">Usage &amp; Costs</h1>
            <p className="text-sm text-slate-500">Per-use activity that drives third-party / AI spend.</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="usage-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {loading && !data && <Card className="p-10 text-center text-slate-500">Loading usage…</Card>}

      {data && (
        <div className="space-y-6">
          {/* Members */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4"><Users className="w-4 h-4 text-slate-500" /><h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Members</h2></div>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
              <Stat label="Total" value={data.members.total} />
              <Stat label="New · 7d" value={data.members.new_7d} />
              <div className="flex flex-wrap gap-2">
                {Object.entries(data.members.by_plan).map(([plan, n]) => (
                  <Badge key={plan} variant="outline" className="border-slate-300 text-slate-700" data-testid={`plan-${plan}`}>{plan}: {n}</Badge>
                ))}
              </div>
            </div>
          </Card>

          {/* Cost events */}
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600 mb-3">Cost-incurring events</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(COST_META).map(([key, meta]) => {
                const c = data.cost_events[key] || { total: 0, last_30d: 0, last_7d: 0, today: 0 };
                const Icon = meta.icon;
                return (
                  <Card key={key} className="p-5" data-testid={`cost-card-${key}`}>
                    <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-md border text-xs font-medium mb-3 ${meta.color}`}>
                      <Icon className="w-3.5 h-3.5" /> {meta.label}
                    </div>
                    <div className="text-3xl font-semibold text-slate-900 tabular-nums">{c.total}</div>
                    <div className="text-xs text-slate-500 mb-3">all time</div>
                    <div className="grid grid-cols-3 gap-1 text-center border-t pt-3">
                      <div><div className="text-sm font-semibold tabular-nums">{c.last_30d}</div><div className="text-[10px] text-slate-400 uppercase">30d</div></div>
                      <div><div className="text-sm font-semibold tabular-nums">{c.last_7d}</div><div className="text-[10px] text-slate-400 uppercase">7d</div></div>
                      <div><div className="text-sm font-semibold tabular-nums">{c.today}</div><div className="text-[10px] text-slate-400 uppercase">Today</div></div>
                    </div>
                    <div className="text-[11px] text-slate-400 mt-3">{meta.billed}</div>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Verifications + Payments */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-6" data-testid="verifications-card">
              <div className="flex items-center gap-2 mb-4"><ShieldCheck className="w-4 h-4 text-slate-500" /><h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Verifications</h2></div>
              <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
                <Stat label="Selfie · pending" value={data.verifications.selfie.pending} />
                <Stat label="Selfie · verified" value={data.verifications.selfie.verified} />
                <Stat label="Selfie · rejected" value={data.verifications.selfie.rejected} />
                <Stat label="Visa · total" value={data.verifications.visa.total} />
                <Stat label="Visa · 30d" value={data.verifications.visa.last_30d} />
              </div>
              <div className="text-[11px] text-slate-400 mt-4">Visa checks are billed per check by the integrated verification provider.</div>
            </Card>

            <Card className="p-6" data-testid="payments-card">
              <div className="flex items-center gap-2 mb-4"><CreditCard className="w-4 h-4 text-slate-500" /><h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Payments (Stripe)</h2></div>
              <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
                <Stat label="Checkouts" value={data.payments.total_checkouts} />
                <Stat label="Paid" value={data.payments.paid} />
                {Object.entries(data.payments.revenue).map(([cur, v]) => (
                  <Stat key={cur} label={`Revenue ${cur.toUpperCase()}`} value={`${CUR_SYMBOL[cur] || ""}${v.amount}`} />
                ))}
                {Object.keys(data.payments.revenue).length === 0 && (
                  <div className="text-sm text-slate-400">No paid transactions yet.</div>
                )}
              </div>
              <div className="text-[11px] text-slate-400 mt-4">Stripe charges a per-transaction fee on each paid plan.</div>
            </Card>
          </div>

          <p className="text-xs text-slate-400 text-right">Generated {new Date(data.generated_at).toLocaleString()}</p>
        </div>
      )}
    </div>
  );
}
